import config from '../../config/config';
import { sessionAwareFetch } from './sessionAwareFetch';

const ANALYTICS_EVENT_TYPES = [
  'storefront_view',
  'service_search',
  'service_view',
  'app_session',
  'phone_click',
  'email_click',
];

/**
 * Send an analytics event to the backend. Fire-and-forget; does not block or throw.
 * phone_click and email_click require the storefront businessId.
 * @param {{ eventType: string, businessId?: string, serviceShortName?: string, subserviceShortName?: string, searchChannel?: 'wizard' | 'ai_search' }} payload
 */
export function trackEvent(payload) {
  if (!config.apimUrl) return;
  const {
    eventType,
    businessId,
    serviceShortName,
    subserviceShortName,
    searchChannel,
  } = payload || {};
  if (!eventType || !ANALYTICS_EVENT_TYPES.includes(eventType)) return;
  const effectiveBusinessId =
    businessId ?? (eventType === 'app_session' ? 'app' : undefined);
  if (!effectiveBusinessId) return;

  const url = `${config.apimUrl}/analytics-api/events`;
  const body = JSON.stringify({
    eventType,
    businessId: effectiveBusinessId,
    ...(serviceShortName && { serviceShortName }),
    ...(subserviceShortName && { subserviceShortName }),
    ...(searchChannel && { searchChannel }),
  });

  const headers = {
    'Content-Type': 'application/json',
    ...(config.apimKeyAnalyticsService && {
      'Ocp-Apim-Subscription-Key': config.apimKeyAnalyticsService,
    }),
  };

  fetch(url, {
    method: 'POST',
    headers,
    body,
    credentials: 'omit',
    keepalive: true,
  }).catch(() => {});
}

/**
 * Fetch analytics summary for a business. Requires auth (cookie/credentials).
 * @param {string} businessId
 * @param {string} [fromDate] YYYY-MM-DD
 * @param {string} [toDate] YYYY-MM-DD
 * @returns {Promise<{ storefrontViews: number, byService: Array, period: { from: string, to: string } }>}
 */
export async function getBusinessAnalytics(businessId, fromDate, toDate) {
  if (!businessId) throw new Error('businessId is required');
  const url = new URL(`${config.apimUrl}/analytics-api/business/${businessId}`);
  if (fromDate) url.searchParams.set('fromDate', fromDate);
  if (toDate) url.searchParams.set('toDate', toDate);

  const response = await sessionAwareFetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(config.apimKeyAnalyticsService && {
        'Ocp-Apim-Subscription-Key': config.apimKeyAnalyticsService,
      }),
    },
    credentials: 'include',
  });

  if (response.status === 403) {
    throw new Error('Forbidden');
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch analytics: ${response.status}`);
  }
  return response.json();
}

/**
 * Fetch app visitor session timestamps (UTC) for the admin dashboard.
 * Pass an inclusive UTC start and exclusive UTC end; the UI buckets into Eastern days.
 * @param {string} fromTs ISO-8601 UTC timestamp (inclusive)
 * @param {string} toTs ISO-8601 UTC timestamp (exclusive)
 * @returns {Promise<{ businessCount: number, accountCount: number, businessLeadCount: number, contactLeadCount: number, leadBreakdown: { inquiryCount: number, phoneClicks: number, emailClicks: number, byBusiness: Array }, sessionTimestamps: string[], period: { from: string, to: string } }>}
 */
export async function getAppVisitorMetrics(fromTs, toTs) {
  const url = new URL(`${config.apimUrl}/analytics-api/app/visitors`);
  if (fromTs) url.searchParams.set('fromTs', fromTs);
  if (toTs) url.searchParams.set('toTs', toTs);

  const response = await sessionAwareFetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(config.apimKeyAnalyticsService && {
        'Ocp-Apim-Subscription-Key': config.apimKeyAnalyticsService,
      }),
    },
    credentials: 'include',
  });

  if (response.status === 401 || response.status === 403) {
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch visitor metrics: ${response.status}`);
  }
  return response.json();
}

export const analyticsService = {
  trackEvent,
  getBusinessAnalytics,
  getAppVisitorMetrics,
};
